import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  type ModelSocialIntegrationEmailParsingRule,
  type ModelSocialIntegrationEmailParsingRuleArray,
  useSocialIntegrationEmailParsingRuleListQuery,
  useSocialIntegrationEmailParsingRuleUpdateMutation,
} from '@app/api/SocialIntegrationEmailParsingRule';
import type { TagOption } from '@app/components/RecordModal/components/RecordDetails/types';
import { getTagsOptions } from '@app/components/RecordModal/components/RecordDetails/utils';
import { INTEGRATIONS_PATH } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useLazyGetAllTagsQuery } from '@app/services/rtkQuery/Tags/api';
import { useErrorHandler } from '@app/shared/hooks';
import type { NTags } from '@app/types';

import { ruleInitialState } from '@pages/IntegrationParsing/data';
import type {
  IntegrationDataType,
  onChangeRuleType,
  RuleType,
  DeletedTagInfo,
} from '@pages/IntegrationParsing/types';

export const useIntegrationParsing = () => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const {
    data: rulesData,
    isLoading: isLoadingRules,
    isFetching: isFetchingRules,
    isUninitialized: isUninitializedRules,
    isError: isErrorRules,
    error: errorRules,
  } = useSocialIntegrationEmailParsingRuleListQuery(
    { ActiveBranchId, socialIntegrationId: +id! },
    { skip: !id, refetchOnMountOrArgChange: true }
  );

  const [updateRule] = useSocialIntegrationEmailParsingRuleUpdateMutation();
  const [getAllTags] = useLazyGetAllTagsQuery();

  const getTagsHandler = useCallback(async () => {
    try {
      const response = await getAllTags({ ActiveBranchId }).unwrap();
      setTags(response);
    } catch (error) {
      showNotification(error);
    }
  }, [showNotification, ActiveBranchId, getAllTags]);

  useErrorHandler([{ flag: isErrorRules, error: errorRules }]);

  const [rules, setRules] = useState<RuleType[]>([ruleInitialState]);
  const [tags, setTags] = useState<NTags.Tag[]>([]);
  const [integrationData, setIntegrationData] =
    useState<IntegrationDataType>(null);

  const tagOptions: TagOption[] = useMemo(() => {
    return getTagsOptions(tags);
  }, [tags]);

  const deletedTags: DeletedTagInfo[] = useMemo(() => {
    if (!rules.length || !tags.length) {
      return [];
    }

    const tagById = new Map<number, NTags.Tag>(
      tags.map((tag) => [tag.Id, tag])
    );
    const deleted = new Map<number, DeletedTagInfo>();

    rules.forEach((rule) => {
      const allRuleTags = [
        ...(rule.FixedTags ?? []),
        ...(rule.AssignTags ?? []),
      ];

      allRuleTags.forEach((tagRef) => {
        if (!tagRef?.Id) return;

        const tag = tagById.get(tagRef.Id);

        if (tag && tag.IsDeleted && !deleted.has(tagRef.Id)) {
          deleted.set(tagRef.Id, {
            Id: tagRef.Id,
            Name: tag.Name ?? tagRef.Name ?? `Тег #${tagRef.Id}`,
          });
        }
      });
    });

    return Array.from(deleted.values());
  }, [rules, tags]);

  const addRule = useCallback(() => {
    setRules((prev) => [...prev, ruleInitialState]);
  }, []);

  const onChangeRule: onChangeRuleType = useCallback((index, field, value) => {
    setRules((prev) =>
      prev.map((rule, i) => (i === index ? { ...rule, [field]: value } : rule))
    );
  }, []);

  const isLoadingPage = useMemo(() => {
    return isLoadingRules || isFetchingRules || isUninitializedRules;
  }, [isLoadingRules, isFetchingRules, isUninitializedRules]);

  const initRules = useCallback(() => {
    if (!rulesData) return;

    const { Rules, ...restIntegrationData } = rulesData;

    const preparedRules =
      Rules && Rules.length > 0 ? Rules : [ruleInitialState];

    setRules(preparedRules);
    setIntegrationData(restIntegrationData);
  }, [rulesData]);

  const goToIntegrations = useCallback(() => {
    navigate(INTEGRATIONS_PATH);
  }, [navigate]);

  const onSave = useCallback(async () => {
    const tagById = new Map<number, NTags.Tag>(
      tags.map((tag) => [tag.Id, tag])
    );

    const formattedRules: ModelSocialIntegrationEmailParsingRule[] =
      rules?.map((rule) => {
        const fixedTagIds =
          rule.FixedTags?.map((tag) => tag.Id).filter((id) => {
            const tag = tagById.get(id);

            return !tag?.IsDeleted;
          }) || [];

        const assignTagIds =
          rule.AssignTags?.map((tag) => tag.Id).filter((id) => {
            const tag = tagById.get(id);
            return !tag?.IsDeleted;
          }) || [];

        return {
          SourceEmailsList: rule.SourceEmailsList ?? [],
          CheckSpam: rule.CheckSpam,
          ExtractFullName: rule.ExtractFullName,
          ExtractPhone: rule.ExtractPhone,
          ExtractGender: rule.ExtractGender,
          ExtractLanguage: rule.ExtractLanguage,
          ExtractEmail: rule.ExtractEmail,
          TagExtractionKeywordList: rule.TagExtractionKeywordList ?? null,
          FixedTagIds: fixedTagIds,
          AssignTagIds: assignTagIds,
          IsOn: rule.IsOn,
        };
      }) || [];

    const request: ModelSocialIntegrationEmailParsingRuleArray = {
      Rules: formattedRules,
      SocialIntegrationId: +id!,
    };

    try {
      await updateRule({ ActiveBranchId, data: request }).unwrap();

      showNotification('Правило обновлено успешно', 'success');
      goToIntegrations();
    } catch (error) {
      showNotification(error);
    }
  }, [
    rules,
    tags,
    ActiveBranchId,
    goToIntegrations,
    showNotification,
    updateRule,
    id,
  ]);

  const onRemove = useCallback((index: number) => {
    setRules((prev) => prev.filter((_, i) => i !== index));
  }, []);

  return {
    rules,
    addRule,
    onChangeRule,
    isLoadingPage,
    initRules,
    goToIntegrations,
    onSave,
    getTagsHandler,
    integrationData,
    tagOptions,
    deletedTags,
    onRemove,
  };
};
